import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { AuthorizationService } from '@modules/authorization/services/authorization.service';
import { ConfirmationService } from '@modules/confirmation/services/confirmation.service';
import { ErrorsService } from '@modules/errors/services/errors.service';
import { InterceptorService } from '@modules/shared/services/interceptor.service';
import { AppState } from '@modules/shared/store';
import { HideSpinnerLoading } from '@modules/shared/store/common.actions';
import { Store } from '@ngrx/store';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { Confirmation, TokenPairResponse } from '@codegen';
import { DefaultService } from '@codegen/service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private authorizationResourceService = inject(DefaultService);
  private store = inject<Store<AppState>>(Store);
  private authorizationService = inject(AuthorizationService);
  private spinner = inject(NgxSpinnerService);
  private errors = inject(ErrorsService);
  private bottomSheet = inject(MatBottomSheet);
  private dialogRef = inject(MatDialog);
  private confirmationService = inject(ConfirmationService);
  private interceptorService = inject(InterceptorService);

  private errorAuthorization: number = 401;
  private errorForbidden: number = 403;
  private pageNotFound: number = 404;
  private internalServerError: number = 500;
  private badGateWay: number = 502;
  private badRequest: number = 400;
  private timeout: number = 504;
  private isRefreshTokenInProgress: boolean;
  private tokenRefreshedSource = new Subject<void>();
  private tokenRefreshed$ = this.tokenRefreshedSource.asObservable();
  private confirmationServices: Confirmation[] = [];

  constructor() {
    this.initializeConfirmationServices();
  }

  private static setHeader(deviceUid: string, clientKind: string): HttpHeaders {
    let header: HttpHeaders = new HttpHeaders().set('DeviceUID', deviceUid);
    header = header.append('ClientKind', clientKind);
    return header;
  }

  public intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const newRequest = request.clone({
      headers: TokenInterceptor.setHeader(
        localStorage.getItem('deviceUid') || '',
        '1'
      ),
    });
    if (
      this.confirmationServices.some((item: Confirmation) =>
        newRequest.url.includes(item.service)
      )
    ) {
      this.confirmationService.setConfirmationComponentOld(
        this.confirmationServices.find((item: Confirmation) =>
          newRequest.url.includes(item.service)
        )
      );
      return this.interceptorService.confirmSms(newRequest).pipe(
        switchMap(() => {
          return next.handle(newRequest).pipe(
            catchError(error => {
              return this.handleResponseError(error, request, next);
            })
          );
        })
      );
    } else {
      return next.handle(newRequest).pipe(
        catchError(error => {
          return this.handleResponseError(error, request, next);
        })
      );
    }
  }

  private handleResponseError(
    error: HttpErrorResponse,
    request?: HttpRequest<unknown>,
    next?: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (error.status === this.errorAuthorization) {
      return this.refreshToken().pipe(
        switchMap(() => {
          request = request.clone({
            headers: TokenInterceptor.setHeader(
              localStorage.getItem('deviceUid') || '',
              '1'
            ),
          });
          return next.handle(request);
        }),
        catchError(e => {
          if (e.status !== this.errorAuthorization) {
            return this.handleResponseError(e);
          }
        })
      );
    } else if (
      error.status === this.internalServerError ||
      error.status === this.badGateWay ||
      error.status === this.badRequest ||
      error.status === this.timeout ||
      error.status === this.errorForbidden ||
      error.status === this.pageNotFound
    ) {
      this.bottomSheet.dismiss();
      this.dialogRef.closeAll();
      this.isRefreshTokenInProgress = false;
      this.store.dispatch(new HideSpinnerLoading());

      this.errors.showError(error, request?.url, {
        actionId: error.error?.actionId,
      });
    }
    return throwError(error);
  }

  private refreshToken(): Observable<void | TokenPairResponse> {
    if (this.isRefreshTokenInProgress) {
      return new Observable(observer => {
        this.tokenRefreshed$.subscribe(() => {
          observer.next();
          observer.complete();
        });
      });
    } else {
      this.isRefreshTokenInProgress = true;
      return this.authorizationResourceService.authRefresh().pipe(
        tap(() => {
          this.isRefreshTokenInProgress = false;
          this.tokenRefreshedSource.next();
        })
      );
    }
  }

  private initializeConfirmationServices(): void {
    this.store
      .select(state => state.common.confirmationServices)
      .pipe(
        filter(services => !!services?.length),
        take(1)
      )
      .subscribe(services => {
        this.confirmationServices.length = 0;
        this.confirmationServices = [...services];
      });
  }
}
